import { useUserOwnMetricsQuery } from '@common/cache/queries/metrics';

const formatTierLabel = (tier: string): string =>
  tier
    .replace(/\bDyad\b/g, 'DYAD')
    .replace(/\s*Tier\s*$/i, '')
    .trim();
import { useState } from 'react';
import useUserStore from 'store/user.store';
import { NonAdminDashboardFilters } from '../../types';
import WarningIcon from '@mui/icons-material/Warning';
import FlagIcon from '@mui/icons-material/Flag';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { theme } from '@store';
import { useMediaQuery } from '@mui/material';

const initialFilters: NonAdminDashboardFilters = {
  interval: 'this_month',
  to_date: '',
  from_date: '',
};

export const useNonAdminView = () => {
  const { user } = useUserStore();
  const [filters, setFilters] =
    useState<NonAdminDashboardFilters>(initialFilters);

  const { data, isLoading } = useUserOwnMetricsQuery({
    interval: filters.interval,
    to_date: filters.to_date,
    from_date: filters.from_date,
  });

  const hasViewPermission = user?.permissions.includes('view_reports');

  const handleFilterChange = (
    newFilters: Partial<NonAdminDashboardFilters>,
  ) => {
    setFilters({ ...filters, ...newFilters });
  };

  const ccmPatientsByMinutes = data?.ccm_patients_by_minutes;
  const careManagementPatientEntries = data?.care_management_patient_counts
    .filter(o => o.patient_count > 0)
    .map(entry => ({ value: entry.patient_count, label: entry.program_name }));
  const serviceLinePatientEntries = data?.service_line_patient_counts
    .filter(o => o.patient_count > 0)
    .map(entry => ({ value: entry.patient_count, label: entry.program_name }));
  const guideStatusPatientsEntries = data?.guide_status_patient_counts
    .filter(o => o.patient_count > 0)
    .map(entry => ({ value: entry.patient_count, label: entry.status }));
  const guideTierPatientsEntries = data?.guide_tier_patient_counts
    .filter(o => o.patient_count > 0)
    .map(entry => ({
      value: entry.patient_count,
      label: formatTierLabel(entry.tier),
    }));
  const memoryConnectStatusPatientsEntries =
    data?.memory_connect_status_patient_counts
      .filter(o => o.patient_count > 0)
      .map(entry => ({
        value: entry.patient_count,
        label: entry.status,
      }));
  const memoryConnectTierPatientsEntries =
    data?.memory_connect_tier_patient_counts
      .filter(o => o.patient_count > 0)
      .map(entry => ({
        value: entry.patient_count,
        label: formatTierLabel(entry.tier),
      }));

  const isMdSizeUp = useMediaQuery('(min-width:1536px)');

  const taskMetricsData = data?.task_metrics;
  const taskMetricsList = [
    {
      value: taskMetricsData?.overdue_tasks_count,
      label: 'Overdue',
      icon: (
        <WarningIcon
          htmlColor={theme.palette.warning.main}
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
    {
      value: taskMetricsData?.urgent_tasks_count,
      label: 'Urgent',
      icon: (
        <FlagIcon
          htmlColor={theme.palette.error.main}
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
    {
      value: taskMetricsData?.high_priority_tasks_count,
      label: 'High priority',
      icon: (
        <FlagIcon
          htmlColor={theme.palette.warning.main}
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
    {
      value: taskMetricsData?.medium_priority_tasks_count,
      label: 'Medium priority',
      icon: (
        <FlagIcon
          htmlColor={theme.palette.info.main}
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
    {
      value: taskMetricsData?.low_priority_tasks_count,
      label: 'Low priority',
      icon: (
        <FlagIcon
          htmlColor={theme.palette.primary.main}
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
    {
      value: taskMetricsData?.created_tasks_count,
      label: 'Created',
      icon: (
        <NoteAddIcon
          htmlColor={theme.palette.primary.main}
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
    {
      value: taskMetricsData?.assigned_tasks_count,
      label: 'Assigned',
      icon: (
        <PersonAddIcon
          htmlColor={theme.palette.primary.main}
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
    {
      value: taskMetricsData?.open_tasks_count,
      label: 'Open',
      icon: (
        <HourglassTopIcon
          htmlColor={theme.palette.primary.main}
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
    {
      value: taskMetricsData?.completed_tasks_count,
      label: 'Completed',
      icon: (
        <CheckCircle
          htmlColor={theme.palette.success.main}
          color='secondary'
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
    {
      value: taskMetricsData?.prematurely_closed_tasks_count,
      label: 'Prematurely closed',
      icon: (
        <Cancel
          htmlColor={theme.palette.success.main}
          color='error'
          sx={{ width: '24px', height: '24px' }}
        />
      ),
    },
  ];

  return {
    user,
    taskMetricsList,
    allMetrics: data,
    filters,
    handleFilterChange,
    guideTierPatientsEntries,
    guideStatusPatientsEntries,
    serviceLinePatientEntries,
    careManagementPatientEntries,
    memoryConnectTierPatientsEntries,
    memoryConnectStatusPatientsEntries,
    hasViewPermission,
    isMdSizeUp,
    ccmPatientsByMinutes,
    isLoading,
  };
};
